import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps, outputType } from 'types/hooks';
import { errorMessage } from 'config/constants';
import { getLenseConfigAnalyticsParser } from 'parser/pricingSimulator/lenseConfigParser';
import { PricingReport, ScenarioComparison } from 'types/pricingSimulator';
import _ from 'lodash';

interface FetchLenseConfigProp extends HookInputProps {
    updateReportProperty: <K extends keyof PricingReport>(key: K, value: PricingReport[K]) => void;
    updateComparisonProperty: <K extends keyof ScenarioComparison>(key: K, value: ScenarioComparison[K]) => void;
    selectedReportInfo: PricingReport | null;
    setSelectedReportInfo: Dispatch<SetStateAction<PricingReport | null>>;
}

function useFetchLenseConfigAnalytics({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
    selectedReportInfo,
    updateReportProperty,
    updateComparisonProperty,
    setSelectedReportInfo,
}: FetchLenseConfigProp) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState(0);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setData(null);
                setError(null);
            }
            setLoading(true);

            try {
                const data = await axios({
                    method,
                    url,
                    params: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const parsedData = getLenseConfigAnalyticsParser(payload);

            //1. loop through change analytics graph items and add lense config
            const result = selectedReportInfo?.changeAnalytics?.input?.map((graphItem) => {
                const clonedLensData = _.cloneDeep(parsedData);
                clonedLensData?.forEach((lens) => {
                    let isKpiSelected = false;
                    lens?.attributes?.forEach((kpi) => {
                        isKpiSelected = kpi?.attributeId === graphItem?.kpiId;
                        kpi.isSelected = isKpiSelected ? true : false;
                        lens.isSelected = lens.isSelected ? true : isKpiSelected ? true : false;
                    });
                });
                graphItem.lensConfig = clonedLensData;
                return graphItem;
            });
            setSelectedReportInfo((prevState: any): PricingReport => {
                return {
                    ...(prevState ?? {}),
                    changeAnalytics: {
                        input: result,
                        output: prevState?.changeAnalytics?.output,
                    },
                };
            });

            // 2. update base kpis ticked based on selectedReportInfo data
            const clonedKpiLensData = _.cloneDeep(parsedData);
            clonedKpiLensData?.forEach((lens) => {
                let isKpiSelected = false;
                lens?.attributes?.forEach((kpi) => {
                    isKpiSelected = selectedReportInfo?.baseKPIs?.selectedKPIs.includes(kpi?.attributeId) || false;
                    kpi.isSelected = isKpiSelected;
                    lens.isSelected = lens.isSelected ? true : isKpiSelected ? true : false;
                });
            });

            if (selectedReportInfo) {
                updateReportProperty('baseKPIs', {
                    ...selectedReportInfo.baseKPIs,
                    kpiConfig: clonedKpiLensData,
                });
            }
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        count,
    };
    return outputData;
}

export { useFetchLenseConfigAnalytics };
